import { useEffect, useState } from 'react';
import { Loader2, Volume2, X } from 'lucide-react';
import { translations } from '../../lib/i18n/translations';
import type { VoiceCall } from '../../lib/voice/hooks';
import { fetchCallAudioUrl } from '../../lib/voice/api';

type VoiceT = typeof translations.en.settings.voice;

interface Props {
  t: VoiceT;
  call: VoiceCall;
  onClose: () => void;
}

/**
 * Player audio della registrazione: on-demand (l'audio viene scaricato via proxy
 * core SOLO al click, mai in automatico aprendo il drawer) + revoca dell'object
 * URL alla chiusura.
 */
function CallAudioPlayer({ t, conversationId }: { t: VoiceT; conversationId: string }) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      setAudioUrl(await fetchCallAudioUrl(conversationId));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (audioUrl) {
    return <audio controls autoPlay src={audioUrl} className="w-full h-9" />;
  }
  return (
    <div>
      <button
        onClick={() => void load()}
        disabled={loading}
        className="text-[12px] text-[#2383E2] flex items-center gap-1.5 hover:underline disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
        {t.calls.playRecording}
      </button>
      {error && <p className="text-[11px] text-[#e03e3e] mt-1">{t.calls.audioError}</p>}
    </div>
  );
}

/**
 * Viewer trascrizione+riassunto della chiamata voice. Leggero: la VoiceCall porta
 * gia' full_transcript/ai_summary inline (source='elevenlabs'), quindi non serve il
 * MeetingDetailDrawer completo (richiede join client/deal/project + onAssociate/onDelete).
 */
export function VoiceCallTranscriptDrawer({ t, call, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="h-full w-full max-w-md bg-white text-[#37352f] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[rgba(55,53,47,0.09)]">
          <h3 className="text-[14px] font-semibold truncate">
            {call.meeting_title || t.calls.transcript}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)]">
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {call.elevenlabs_conversation_id && (
            <section>
              <h4 className="text-[11px] uppercase tracking-wide text-[#9b9a97] mb-1.5">
                {t.calls.recording}
              </h4>
              <CallAudioPlayer t={t} conversationId={call.elevenlabs_conversation_id} />
            </section>
          )}

          <section>
            <h4 className="text-[11px] uppercase tracking-wide text-[#9b9a97] mb-1.5">{t.calls.summary}</h4>
            {call.ai_summary ? (
              <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{call.ai_summary}</p>
            ) : (
              <p className="text-[13px] text-[#9b9a97] italic">{t.calls.noSummary}</p>
            )}
          </section>

          <section>
            <h4 className="text-[11px] uppercase tracking-wide text-[#9b9a97] mb-1.5">{t.calls.transcript}</h4>
            {call.full_transcript ? (
              <pre className="text-[12px] leading-relaxed whitespace-pre-wrap font-sans text-[#37352f]">
                {call.full_transcript}
              </pre>
            ) : (
              <p className="text-[13px] text-[#9b9a97] italic">{t.calls.noTranscript}</p>
            )}
          </section>
        </div>

        <div className="px-5 py-3 border-t border-[rgba(55,53,47,0.09)] flex justify-end">
          <button
            onClick={onClose}
            className="text-[13px] text-[#37352f] px-3 py-1.5 hover:bg-[rgba(55,53,47,0.06)] rounded"
          >
            {t.calls.close}
          </button>
        </div>
      </div>
    </div>
  );
}
